import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { byName } from "@/lib/status";
import { api, errMsg } from "@/lib/api";
import { usePractice } from "@/pages/StaffApp";

export function useActiveForms(enabled = true) {
  const [forms, setForms] = useState([]);
  useEffect(() => {
    if (!enabled) return;
    api.get("/templates").then((r) => {
      const fams = {};
      r.data.filter((t) => t.status === "active").forEach((t) => { (fams[t.family_id] = fams[t.family_id] || []).push(t); });
      setForms(Object.values(fams).map((g) => g.find((x) => x.language === "en-US") || g[0]).sort((a, b) => a.name.localeCompare(b.name)));
    }).catch(() => {});
  }, [enabled]);
  return forms;
}

export function FormPicker({ forms, value, onChange, testid }) {
  return (
    <div className="grid sm:grid-cols-2 gap-2" data-testid={testid}>
      {forms.map((t) => (
        <label key={t.family_id} className="flex items-center gap-2 border rounded-md p-2.5 text-sm cursor-pointer hover:bg-slate-50 transition-colors">
          <Checkbox checked={value.includes(t.family_id)} onCheckedChange={(v) => onChange(v ? [...value, t.family_id] : value.filter((x) => x !== t.family_id))} data-testid={`${testid}-${t.family_id}`} />
          <span>{t.name} <span className="text-slate-500">v{t.version}</span></span>
        </label>))}
      {!forms.length && <p className="text-sm text-slate-500">No active, approved forms yet.</p>}
    </div>
  );
}

function Card({ title, sub, forms, saved, scope, code, onDone, clearLabel }) {
  const [v, setV] = useState(saved);
  useEffect(() => { setV(saved); }, [saved]);
  const dirty = [...v].sort().join() !== [...saved].sort().join();
  const save = async (ids) => {
    const body = { scope, family_ids: ids, ...(scope === "facility" ? { facility_code: code } : scope === "provider" ? { provider_code: code } : {}) };
    try { await api.put("/default-packets", body); toast.success("Default packet saved"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const id = code ? `${scope}-${code}` : "org";
  return (
    <div className="bg-white border rounded-xl p-4 space-y-3" data-testid={`default-packet-${id}`}>
      <div className="flex flex-wrap items-start gap-2"><div className="flex-1 min-w-[200px]"><h3 className="font-semibold">{title}</h3><p className="text-xs text-slate-500">{sub}</p></div>
        {clearLabel && saved.length > 0 && <Button size="sm" variant="outline" onClick={() => save([])} data-testid={`default-packet-clear-${id}`}>{clearLabel}</Button>}
        <Button size="sm" className="btn-brand" disabled={!dirty} onClick={() => save(v)} data-testid={`default-packet-save-${id}`}>Save</Button></div>
      <FormPicker forms={forms} value={v} onChange={setV} testid={`default-packet-forms-${id}`} />
    </div>
  );
}

export default function DefaultPackets() {
  const { practice } = usePractice();
  const forms = useActiveForms();
  const [d, setD] = useState(null);
  const [fq, setFq] = useState("");
  const load = () => api.get("/default-packets").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (!d) return <p className="text-sm text-slate-500">Loading...</p>;
  const names = (ids) => forms.filter((f) => ids.includes(f.family_id)).map((f) => f.name).join(", ");
  return (
    <div className="space-y-4" data-testid="default-packets">
      <div><h2 className="text-lg font-semibold">Default packets</h2>
        <p className="text-sm text-slate-600">When a patient is added: the provider's own default is used first (only for providers allowed to have one), then the facility default, then the organization default. The packet goes to the pending list in the patient's language when a translated version exists.</p></div>
      <Card title={`Organization default - ${practice.name}`} sub="Used for every facility that has no default of its own." forms={forms} saved={d.organization} scope="organization" onDone={load} />
      <div className="flex flex-wrap items-center gap-3 pt-2"><h3 className="font-semibold">Facility defaults (optional)</h3>
        <div className="relative w-72"><Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" /><Input value={fq} onChange={(e) => setFq(e.target.value)} placeholder="Search facility by name or code" className="h-9 pl-8" data-testid="default-packet-facility-search" /></div></div>
      {(() => { const fs = [...practice.facilities].sort(byName).filter((f) => `${f.name} ${f.code}`.toLowerCase().includes(fq.toLowerCase())); return fs.length ? fs : [null]; })().map((f) => {
        if (!f) { return <p key="none" className="text-sm text-slate-500" data-testid="default-packet-facility-none">No facilities match "{fq}".</p>; }
        const own = d.facilities[f.code] || [];
        return <Card key={f.code} title={f.name} code={f.code} scope="facility" forms={forms} saved={own} onDone={load} clearLabel="Use organization default"
          sub={own.length ? "Uses its own default packet." : `Uses the organization default${d.organization.length ? `: ${names(d.organization)}` : " (none set)"}.`} />;
      })}
      <h3 className="font-semibold pt-2">Provider defaults (optional)</h3>
      <p className="text-xs text-slate-500 -mt-2">Overrides the facility default for that provider's patients. Providers can edit their own only when allowed in Authorization Manager.</p>
      {practice.providers.map((p) => {
        const own = d.providers?.[p.code] || [];
        return <Card key={p.code} title={p.name} code={p.code} scope="provider" forms={forms} saved={own} onDone={load} clearLabel="Use facility default"
          sub={own.length ? "Uses the provider's own default packet." : "Uses the facility / organization default."} />;
      })}
    </div>
  );
}
